<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NTooltip } from 'naive-ui'
import EmptyStateIllustration from '@/components/commons/EmptyStateIllustration.vue'
import type { PursuitRankerItem } from '@/api/pursuit'
import { formatScore, formatScoreBreakdown } from '@/utils/formatScore'
import { formatDateTime } from '@/utils/formatDate'
import { normalizeLatinToCyr } from '@/utils/latinToCyr'
import { useScoreZone } from '@/composables/useScoreZone'

const { zoneClass } = useScoreZone()

const props = defineProps<{
  items: PursuitRankerItem[]
  loading: boolean
}>()

const router = useRouter()
const route = useRoute()

function onRowClick(plate: string) {
  const cyr = normalizeLatinToCyr(plate.toUpperCase())
  router.push({
    name: 'pursuit-plate',
    params: { plate: cyr },
    query: route.query,
  })
}

const sortedItems = computed(() => [...props.items].sort((a, b) => b.score - a.score))
</script>

<template>
  <div>
    <table v-if="items.length > 0" class="pursuit-table">
      <thead>
        <tr>
          <th class="num">#</th>
          <th>Номер</th>
          <th class="num">Балл</th>
          <th class="num">Эпизодов</th>
          <th class="num">Машин</th>
          <th class="num">Передняя</th>
          <th class="num">Задняя</th>
          <th>Последняя детекция</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, idx) in sortedItems"
          :key="item.plate"
          :data-plate="item.plate"
          class="pursuit-row"
          @click="onRowClick(item.plate)"
        >
          <td class="num">{{ idx + 1 }}</td>
          <td class="plate">{{ item.plate }}</td>
          <td class="num">
            <n-tooltip>
              <template #trigger>
                <span class="score-val" :class="zoneClass(item.score)">{{ formatScore(item.score) }}</span>
              </template>
              {{ formatScoreBreakdown(item) }}
            </n-tooltip>
          </td>
          <td class="num">{{ item.appearances }}</td>
          <td class="num">{{ item.unique_cars }}</td>
          <td class="num">{{ item.front_count }}</td>
          <td class="num">{{ item.rear_count }}</td>
          <td>{{ formatDateTime(item.last_seen) }}</td>
        </tr>
      </tbody>
    </table>
    <EmptyStateIllustration
      v-else
      type="no-pursuits"
      title="Преследователей не обнаружено"
      description="Попробуйте расширить окно времени или снизить мин. число эпизодов."
    />
  </div>
</template>

<style scoped>
.pursuit-table {
  width: 100%;
  border-collapse: collapse;
}
.pursuit-table th,
.pursuit-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid var(--n-border-color, #2a2a4e);
}
.pursuit-table th.num,
.pursuit-table td.num {
  text-align: right;
}
.pursuit-row {
  cursor: pointer;
}
.pursuit-row:hover {
  background: var(--n-hover-color, rgba(255, 255, 255, 0.04));
}
.plate {
  font-family: ui-monospace, monospace;
  font-weight: 600;
}
/* Цветовые зоны score (E2): пороги настраиваются на странице «Настройки». */
.score-val { font-weight: 600; }
.score-green { color: var(--success, #3aa657); }
.score-yellow { color: var(--warning, #d99a16); }
.score-red { color: var(--danger, #e5484d); }
</style>
